/**
 * The route table — every URL the app answers, in one place.
 *
 * Pages are lazy-loaded so each one ships as its own chunk and the first
 * paint only pays for the shell plus the screen being opened. Login and the
 * layout stay eager: they are the entry experience and must never flash a
 * loading state of their own.
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Login from './pages/auth/Login';
import Signup from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import AppLayout from './components/layout/AppLayout';
import LegacyRedirect from './components/layout/LegacyRedirect';
import { Loading } from './components/ui';
import { PROFILES, ROUTES } from './config/navigation';
import { useDocumentTheme } from './hooks';

const Landing = lazy(() => import('./pages/Landing'));
const Pricing = lazy(() => import('./pages/Pricing'));
const AppReturn = lazy(() => import('./pages/AppReturn'));

const Dashboard = lazy(() => import('./pages/Dashboard'));
const IndividualHome = lazy(() => import('./pages/individual/Home'));
const Client360 = lazy(() => import('./pages/Client360'));
const Goals = lazy(() => import('./pages/Goals'));
const GoalDetail = lazy(() => import('./pages/GoalDetail'));
const TradingGoals = lazy(() => import('./pages/TradingGoals'));
const TradePortfolio = lazy(() => import('./pages/TradePortfolio'));
const Portfolios = lazy(() => import('./pages/Portfolios'));
const PortfolioDeepDive = lazy(() => import('./pages/PortfolioDeepDive'));
const Mandates = lazy(() => import('./pages/Mandates'));
const ModelDetail = lazy(() => import('./pages/ModelDetail'));
const Research = lazy(() => import('./pages/Research'));
const Watchlist = lazy(() => import('./pages/Watchlist'));
const ReportViewer = lazy(() => import('./pages/ReportViewer'));
const Compare = lazy(() => import('./pages/Compare'));
/* The Analyzer and Deep Insights are one screen now — both paths still answer,
   so every deep link written against either of them keeps working. */
const PortfolioInsights = lazy(() => import('./pages/PortfolioInsights'));
const AttributionReports = lazy(() => import('./pages/AttributionReports'));
const Studio = lazy(() => import('./pages/Studio'));
const Proposals = lazy(() => import('./pages/Proposals'));
const ProposalDetail = lazy(() => import('./pages/ProposalDetail'));
const Transactions = lazy(() => import('./pages/Transactions'));
const CasImport = lazy(() => import('./pages/CasImport'));
const Empanelment = lazy(() => import('./pages/Empanelment'));
const Kyc = lazy(() => import('./pages/Kyc'));
const Team = lazy(() => import('./pages/Team'));
const Compliance = lazy(() => import('./pages/Compliance'));
const KycDetail = lazy(() => import('./pages/KycDetail'));
const Firm = lazy(() => import('./pages/Firm'));
const FundDeepDive = lazy(() => import('./pages/FundDeepDive'));
const MyFunds = lazy(() => import('./pages/MyFunds'));
const ManagerDesk = lazy(() => import('./pages/ManagerDesk'));
const AlphaModel = lazy(() => import('./pages/AlphaModel'));
const StockAlpha = lazy(() => import('./pages/StockAlpha'));
const Desk = lazy(() => import('./pages/Desk'));
const Stocks = lazy(() => import('./pages/Stocks'));
const StockDetail = lazy(() => import('./pages/StockDetail'));
const Settings = lazy(() => import('./pages/Settings'));

/* The Keep Investing staff console — app_admin's screens, reachable only from
   the employee seat. Lazy like everything else, so no other seat ever
   downloads them. */
const Configuration = lazy(() => import('./pages/admin/Configuration'));
const AgenticCosts = lazy(() => import('./pages/admin/AgenticCosts'));
const SupportConsole = lazy(() => import('./pages/admin/SupportConsole'));
const SupportCall = lazy(() => import('./pages/admin/SupportCall'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const Employees = lazy(() => import('./pages/admin/Employees'));
const Prospects = lazy(() => import('./pages/admin/Prospects'));
const AgentQuality = lazy(() => import('./pages/admin/AgentQuality'));
const Profile = lazy(() => import('./pages/Profile'));

/**
 * Signed out on an app route: someone who just signed out goes to the
 * marketing page, anyone else to the sign-in form.
 *
 * The guard picks the destination rather than the sign-out handler, so there
 * is only ever one navigation. When the handler navigated too, whichever
 * update React committed first won — and it was usually this redirect, which
 * is why sign-out kept ending on the login form.
 */
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, signedOut } = useSelector((state) => state.auth);
  if (!isLoggedIn) return <Navigate to={signedOut ? '/' : '/login'} replace />;
  return children;
};

/** Role-aware home: MFD → dashboard, PM → fund, CIO → firm, PMS RM → mandates. */
const RoleLanding = () => {
  const role = useSelector((s) => s.ui.role);
  return <Navigate to={ROUTES[PROFILES[role].landing]} replace />;
};

export default function AppRoutes() {
  /* Theme and palette onto <html>. Here rather than in the app shell: the
     marketing and auth screens render outside it and share the setting. */
  useDocumentTheme();

  return (
    <Suspense fallback={<Loading rows={4} label="Loading…" />}>
      <Routes>
        {/* Public. `/` is the marketing page whatever the session, which is
            what makes signing out a plain navigation: nothing can redirect
            off it mid-flight the way a guarded index route did. */}
        <Route path="/" element={<Landing />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        {/* Public, and they must be: somebody who cannot sign in is the only
            person who needs them. `/reset-password` is not our name for it —
            it is the address the backend puts in the reset email. */}
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/pricing" element={<Pricing />} />
        {/* Public. The KYC provider's redirect URLs for mobile-app users:
            /app/aadhaar-callback and /app/esign-callback. */}
        <Route path="/app/:step" element={<AppReturn />} />

        <Route
          element={
            <ProtectedRoute>
              <AppLayout />
            </ProtectedRoute>
          }
        >
          <Route path="dashboard" element={<Dashboard />} />
          {/* An individual's own home — the landing of the individual seat. */}
          <Route path="home" element={<IndividualHome />} />
          {/* The client book lives on the dashboard now — one screen, not two.
              The path stays because it is linked from half the app, and from
              anything a user bookmarked. */}
          <Route path="clients" element={<Navigate to="/dashboard" replace />} />
          <Route path="clients/:id" element={<Client360 />} />
          <Route path="goals" element={<Goals />} />
          <Route path="goals/:goalId" element={<GoalDetail />} />
          <Route path="trading-goals" element={<TradingGoals />} />
          <Route path="trade-portfolio" element={<TradePortfolio />} />
          {/* Every portfolio on the book, and one portfolio in full — opened
              from this list or from a client's Portfolio tab. */}
          <Route path="portfolios" element={<Portfolios />} />
          <Route path="portfolios/:symbol" element={<PortfolioDeepDive />} />
          <Route path="mandates" element={<Mandates />} />
          <Route path="models/:id" element={<ModelDetail />} />
          <Route path="research" element={<Research />} />
          <Route path="watchlist" element={<Watchlist />} />
          {/* A published report, read in the app rather than handed to the
              browser's own PDF viewer. */}
          <Route path="reports/view" element={<ReportViewer />} />
          <Route path="compare" element={<Compare />} />
          <Route path="analyzer" element={<PortfolioInsights />} />
          <Route path="attribution" element={<AttributionReports />} />
          <Route path="studio" element={<Studio />} />
          <Route path="insights" element={<PortfolioInsights />} />
          <Route path="proposals" element={<Proposals />} />
          <Route path="proposals/:urlId" element={<ProposalDetail />} />
          <Route path="transactions" element={<Transactions />} />
          {/* A client's own statement, read in the browser. Nothing is stored,
              so the screen holds no id and takes no route param. */}
          <Route path="cas" element={<CasImport />} />
          <Route path="empanelment" element={<Empanelment />} />
          <Route path="compliance" element={<Compliance />} />
          <Route path="kyc" element={<Kyc />} />
          <Route path="kyc/:id" element={<KycDetail />} />
          <Route path="firm" element={<Firm />} />
          <Route path="funds" element={<MyFunds />} />
          <Route path="funds/:id" element={<FundDeepDive />} />
          <Route path="managers/:name" element={<ManagerDesk />} />
          <Route path="alpha" element={<AlphaModel />} />
          {/* The Alpha Desk: `/desk/{workspace}/{tab}`, the tab optional so a
              workspace link opens on its first tab (config/desk.js). */}
          <Route path="desk" element={<Navigate to="/desk/ideas/today" replace />} />
          <Route path="desk/:workspace/:tab?" element={<Desk />} />
          {/* The stock-alpha pages and the stock master's own page redirect
              into the desk with their context while DESK_REDIRECTS_ON; off,
              they still answer as they always did. */}
          <Route path="stock-alpha" element={<Navigate to="/stock-alpha/today" replace />} />
          <Route path="stock-alpha/:screen" element={<LegacyRedirect><StockAlpha /></LegacyRedirect>} />
          <Route path="stocks" element={<Stocks />} />
          <Route path="stocks/:symbol" element={<LegacyRedirect><StockDetail /></LegacyRedirect>} />
          <Route path="settings" element={<Settings />} />
          <Route path="team" element={<Team />} />
          <Route path="profile" element={<Profile />} />

          {/* Keep Investing staff. The seat guard is what protects these —
              a non-staff seat has no `config`/`support`/… in its allowed set,
              so AppLayout sends it back to its own landing. */}
          <Route path="admin/configuration" element={<Configuration />} />
          <Route path="admin/agentic-costs" element={<AgenticCosts />} />
          <Route path="admin/support" element={<SupportConsole />} />
          <Route path="admin/support/:ticketId" element={<SupportCall />} />
          <Route path="admin/users" element={<AdminUsers />} />
          <Route path="admin/employees" element={<Employees />} />
          <Route path="admin/prospects" element={<Prospects />} />
          <Route path="admin/agent-quality" element={<AgentQuality />} />
          <Route path="*" element={<RoleLanding />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
